(function () {
  const widget = document.querySelector("aitaca-widget");
  if (!widget) return;

  const variants = window.__aitacaProductVariants;
  if (!variants?.length) {
    console.error("Aitaca: No product variants found.");
    return;
  }

  // ─── Layer 1: Read the selected variant from the URL query string ─────────
  function getVariantFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get("variant");
    if (!variantId) return null;
    return variants.find((v) => String(v.id) === variantId) || null;
  }

  // ─── Layer 2: Read an option value by position from the theme's DOM ───────
  function getOptionFromDom(position) {
    const selectors = [
      `select[name="options[${position}]"]`,
      `input[type="radio"][name="options[${position}]"]:checked`,
      `[data-option-index="${position - 1}"][aria-checked="true"]`,
      `[data-option-index="${position - 1}"].selected`,
      `[data-option-position="${position}"].active`,
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element.value || element.dataset.value || element.textContent.trim();
      }
    }

    return null;
  }

  // ─── Resolve options 2 & 3 using the best available source ────────────────
  function resolveOptions(option1Value) {
    // Layer 1 — URL (highest priority)
    const urlVariant = getVariantFromUrl();
    if (urlVariant) {
      return {
        option1: option1Value,
        option2: urlVariant.option2,
        option3: urlVariant.option3,
      };
    }

    // Layer 2 — Theme DOM elements
    const domOption2 = getOptionFromDom(2);
    const domOption3 = getOptionFromDom(3);
    if (domOption2 || domOption3) {
      return {
        option1: option1Value,
        option2: domOption2,
        option3: domOption3,
      };
    }

    // Layer 3 — Liquid-injected initial variant (page-load state)
    const initialVariant = window.__aitacaInitialVariant;
    return {
      option1: option1Value,
      option2: initialVariant?.option2 ?? null,
      option3: initialVariant?.option3 ?? null,
    };
  }

  // ─── Find the variant that matches the given option combination ───────────
  function findMatchingVariant({ option1, option2, option3 }) {
    return (
      variants.find((variant) => {
        const isOption1Match = variant.option1 === option1;
        const isOption2Match = option2 ? variant.option2 === option2 : true;
        const isOption3Match = option3 ? variant.option3 === option3 : true;
        return isOption1Match && isOption2Match && isOption3Match;
      }) ?? null
    );
  }

  // ─── Detect native sections from theme ────────────────────────────────────
  function detectThemeSections() {
    const sectionIds = [
      "cart-notification-product",
      "cart-notification-message",
      "cart-notification-button",
      "cart-icon-bubble",
    ];
    return sectionIds.filter((id) => document.getElementById(id) !== null);
  }

  // ─── Build the localization-aware cart add URL ────────────────────────────
  // The theme injects a global `routes` object via Liquid with already-localized
  // paths (e.g. /es/cart/add). We append .js to get the JSON endpoint.
  // Fallback uses Shopify.routes.root which is also localized.
  function getCartAddUrl() {
    if (window.routes?.cart_add_url) {
      const url = window.routes.cart_add_url;
      return url.endsWith(".js") ? url : url + ".js";
    }
    const root = window.Shopify?.routes?.root ?? "/";
    return root.replace(/\/$/, "") + "/cart/add.js";
  }

  // ─── Try to render native section from theme ──────────────────────────────
  function tryNativeSections(parsedState) {
    const cartNotification = document.querySelector("cart-notification");
    const cartDrawer = document.querySelector("cart-drawer");

    if (cartNotification && parsedState.sections && Object.keys(parsedState.sections).length > 0) {
      try {
        cartNotification.renderContents(parsedState);
        return true;
      } catch (err) {
        console.warn("Aitaca: renderContents failed, using fallback —", err);
        return false;
      }
    }

    if (cartDrawer) {
      if (typeof cartDrawer.open === "function") {
        cartDrawer.open();
        return true;
      }
    }

    return false;
  }

  // ─── Fallback toast notification ──────────────────────────────────────────
  function showToast(message) {
    const existing = document.getElementById("aitaca-toast");
    if (existing) existing.remove();

    const toast = document.createElement("div");
    toast.id = "aitaca-toast";
    toast.setAttribute("role", "status");
    toast.textContent = message || "Product added to cart";

    Object.assign(toast.style, {
      position: "fixed",
      bottom: "1.25rem",
      right: "1.25rem",
      zIndex: "2147483647",
      padding: "0.75rem 1.25rem",
      borderRadius: "6px",
      fontSize: "14px",
      fontFamily: "inherit",
      backgroundColor: "#000",
      color: "#fff",
      boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
      opacity: "1",
      transition: "opacity 0.3s ease",
      pointerEvents: "none",
    });

    document.body.appendChild(toast);
    setTimeout(() => { toast.style.opacity = "0"; }, 2500);
    setTimeout(() => toast.remove(), 2800);
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────
  function addToCart(variantId) {
    const cartNotification = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
    if (cartNotification) cartNotification.setActiveElement(document.activeElement);
    const cartAddUrl = getCartAddUrl();
    const sections = detectThemeSections();

    return fetch(cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        ...(sections.length > 0 && {
          sections,
          sections_url: window.location.pathname,
        }),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          console.error("Aitaca: error adding to cart —", data.description);
          return;
        }

        // Dispatch custom events for theme compatibility
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        document.documentElement.dispatchEvent(
          new CustomEvent("cart-update", { bubbles: true })
        );
        document.dispatchEvent(
          new CustomEvent("aitaca:added-to-cart", {
            bubbles: true,
            detail: { variantId },
          })
        );

        // Try native theme cart UI, fall back to toast if unavailable
        if (!tryNativeSections(data)) {
          showToast();
        }
      });
  }

  // ─── Main event listener ──────────────────────────────────────────────────
  widget.addEventListener("on_add_to_cart", (event) => {
    const { size } = event.detail;
    const option1Value = String(size);

    const resolvedOptions = resolveOptions(option1Value);
    const targetVariant = findMatchingVariant(resolvedOptions);

    if (!targetVariant) {
      console.error("Aitaca: No variant found for options —", resolvedOptions);
      return;
    }

    if (!targetVariant.available) {
      console.warn("Aitaca: Variant is unavailable —", targetVariant);
      return;
    }

    addToCart(targetVariant.id).catch((error) =>
      console.error("Aitaca: Network error adding to cart —", error)
    );
  });
})();